import { useCallback, useEffect } from 'react';
import type { FieldErrors, FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form';
import type { TDocumentGenerationFormProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import {
  focusDocumentGenerationField,
  getDocumentGenerationValidationIssues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/validation.ts';

type TUseDocumentGenerationFormSubmissionParams<TValues extends FieldValues> = Pick<
  TDocumentGenerationFormProps,
  'onDirtyChange' | 'onSaveAttemptChange' | 'onValidationIssuesChange'
> & {
  form: UseFormReturn<TValues>;
  onValidSubmit: SubmitHandler<TValues>;
  requiredFieldNames?: string[];
};

export const useDocumentGenerationFormSubmission = <TValues extends FieldValues>({
  form,
  onDirtyChange,
  onSaveAttemptChange,
  onValidationIssuesChange,
  onValidSubmit,
  requiredFieldNames = [],
}: TUseDocumentGenerationFormSubmissionParams<TValues>) => {
  const { errors, isDirty } = form.formState;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onValidationIssuesChange?.(getDocumentGenerationValidationIssues(errors, requiredFieldNames));
  }, [errors, onValidationIssuesChange, requiredFieldNames]);

  const handleInvalidSubmit = useCallback(
    (invalidErrors: FieldErrors<TValues>) => {
      const issues = getDocumentGenerationValidationIssues(invalidErrors, requiredFieldNames);
      const fieldName = issues.requiredFieldNames[0] ?? issues.invalidFieldNames[0];

      if (fieldName) {
        focusDocumentGenerationField(fieldName);
      }
    },
    [requiredFieldNames]
  );

  const attemptSave = useCallback(() => {
    void form.handleSubmit(onValidSubmit, handleInvalidSubmit)();
  }, [form, handleInvalidSubmit, onValidSubmit]);

  useEffect(() => {
    onSaveAttemptChange?.(attemptSave);

    return () => onSaveAttemptChange?.(null);
  }, [attemptSave, onSaveAttemptChange]);

  return { attemptSave };
};
